import { BUYER_PHONE_INVALID_MESSAGE, isValidBuyerPhone } from '@massilia/contracts'
import { firstNameSchemaV4, lastNameSchemaV4 } from '@massilia/features'
import { addDays, differenceInYears, format, isBefore, parse } from 'date-fns'
import { z } from 'zod/v4'

import type { PassengersCount } from '@/routes/informations/-utils/constants'

import { getPassengerCategory } from '@/routes/informations/-utils/constants'

const optionSchema = z.object({ label: z.string(), value: z.string() })

const identityDocumentSchema = z
  .object({
    documentNumber: z.string().min(1, 'Le numéro de document est requis'),
    expiryDate: z.string().min(1, "La date d'expiration est requise"),
    issuingCountry: optionSchema.refine(
      (opt) => opt.value.length > 0,
      "Le pays d'émission est requis"
    ),
    type: z.enum(['CNI', 'PASSPORT']),
  })
  .optional()

const fidelityCardSchema = z
  .object({
    companyName: optionSchema.refine(
      (opt) => opt.value.length > 0,
      'Le nom de la compagnie est requis'
    ),
    cardNumber: z.string().min(1, 'Le numéro de carte est requis'),
  })
  .optional()

const informationsPassengerSchema = z.object({
  firstName: firstNameSchemaV4,
  lastName: lastNameSchemaV4,
  savedPassengerId: z.string().nullable().default(null),
  birthDay: z.string('Le jour de naissance est requis').min(1, 'Le jour de naissance est requis'),
  birthMonth: z.string('Le mois de naissance est requis').min(1, 'Le mois de naissance est requis'),
  birthYear: z
    .string("L'année de naissance est requise")
    .min(1, "L'année de naissance est requise"),
  fidelityCard: fidelityCardSchema,
  gender: z.enum(['MALE', 'FEMALE'], 'Veuillez sélectionner le sexe du voyageur'),
  identityDocument: identityDocumentSchema,
  nationality: optionSchema.refine((opt) => opt.value.length > 0, 'La nationalité est requise'),
  retrieveFromSaved: z.boolean().default(false),
})

const informationsSchema = z.object({
  buyerEmail: z.string().min(1, "L'email est requis").email('Email invalide'),
  acceptsDepositCommitment: z
    .boolean()
    .refine(
      (v) => v === true,
      "Vous devez vous engager à verser l'acompte ou la totalité dans les délais indiqués"
    ),
  acceptsTermsAndConditions: z
    .boolean()
    .refine((v) => v === true, 'Vous devez accepter les conditions générales de vente'),
  buyerPhone: z
    .string('Le téléphone est requis')
    .trim()
    .min(1, 'Le téléphone est requis')
    .refine(isValidBuyerPhone, BUYER_PHONE_INVALID_MESSAGE),
  passengers: z.array(informationsPassengerSchema).min(1, 'Au moins un voyageur est requis'),
})

// --- Dynamic validation ---

interface InformationsValidationContext {
  firstFlightDepartureDate: string // format "YYYY-MM-DD"
  lastFlightArrivalDate: string // format "YYYY-MM-DD"
  passengersCount: PassengersCount
}

const CATEGORY_AGE_RANGES: Record<string, { label: string; max: number; min: number }> = {
  adults: { label: 'un adulte (25-59 ans)', max: 59, min: 25 },
  babies: { label: 'un bébé (moins de 2 ans)', max: 1, min: 0 },
  infants: { label: 'un enfant (2-11 ans)', max: 11, min: 2 },
  seniors: { label: 'un senior (60 ans ou plus)', max: Infinity, min: 60 },
  youngsters: { label: 'un jeune (12-24 ans)', max: 24, min: 12 },
}



export type InformationsPassengerSchema = z.infer<typeof informationsPassengerSchema>


export function createInformationsSchema(context: InformationsValidationContext) {
  return informationsSchema.check((ctx) => {
    const departureDate = parse(context.firstFlightDepartureDate, 'yyyy-MM-dd', new Date())
    const referenceDate = parse(context.lastFlightArrivalDate, 'yyyy-MM-dd', new Date())
    const minExpiryDate = addDays(referenceDate, 1)

    ctx.value.passengers.forEach((passenger, index) => {
      // Validation 1: Identity document must not be expired at return date + 1 day
      if (passenger.identityDocument?.expiryDate) {
        const expiryDate = passenger.identityDocument.expiryDate
        if (isBefore(expiryDate, minExpiryDate)) {
          ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            message: `Le document d'identité doit être valide au moins jusqu'au ${format(minExpiryDate, 'dd/MM/yyyy')} (lendemain du retour)`,
            path: ['passengers', index, 'identityDocument', 'expiryDate'],
          })
        }
      }

      // Validation 2: Birth date must match passenger category age range
      if (passenger.birthYear && passenger.birthMonth && passenger.birthDay) {
        const birthDate = parse(
          `${passenger.birthYear}-${passenger.birthMonth}-${passenger.birthDay}`,
          'yyyy-MM-dd',
          new Date()
        )
        const category = getPassengerCategory(context.passengersCount, index)
        const ageRange = CATEGORY_AGE_RANGES[category]
        if (ageRange) {
          const age = differenceInYears(departureDate, birthDate)
          if (age < ageRange.min || age > ageRange.max) {
            ctx.issues.push({
              code: 'custom',
              input: ctx.value,
              message: `La date de naissance ne correspond pas à ${ageRange.label}. Le voyageur aura ${age} ans à la date du voyage`,
              path: ['passengers', index, 'birthYear'],
            })
          }
        }
      }
    })
  })
}
